/**
 * Records and reports: what can be evidenced about this repository.
 *
 * Called "Audit & reporting" until the redesign — "audit" reads as an event
 * people dread rather than a screen they can open on any ordinary day, and the
 * thing it actually holds is the record of what happened.
 *
 * Two rules shape it, and both are about not overstating what we know.
 *
 * 1. **A number nobody can substantiate is not shown.** The acknowledgement
 *    report returns `population_resolved: false` when a role's membership could
 *    not be resolved — the server refuses to report a falsely complete list,
 *    and a percentage computed over an unresolved population would put that
 *    exact falsehood back. Those assignments are excluded from the tile and
 *    counted separately, in words.
 * 2. **The tiles are backed by data that is actually written.** It is easy to
 *    add a "restricted-access exceptions" tile; it is only honest if something
 *    writes that event. What the access log really records is `reveal` — a
 *    protected value being disclosed to somebody entitled to it — so that is
 *    what the tile counts, and it says so.
 *
 * Both calls behind this view are administrator-only on the server. A caller
 * who is not one never gets here: the host probes once and hides the tab.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  auditActionLabel, auditModuleOf, AUDIT_MODULE_LABELS, formatWhen,
  type DmsAckReportRow, type DmsAuditEntry, type DmsClient, type DmsUser,
} from './contracts'
import { personName } from './people'
import {
  btnStyle as sharedBtnStyle, inputStyle as sharedInputStyle, RADIUS, T, TYPE,
} from './styles'

export interface ComplianceViewProps {
  client: DmsClient
  onOpen: (documentId: string) => void
  /** The directory, so the "Who" column names people rather than listing the
   *  login keys the log stores. Optional: without it, keys are shown. */
  users?: DmsUser[]
}

const WINDOWS: Array<{ key: string; label: string; days: number }> = [
  { key: '7', label: 'Last 7 days', days: 7 },
  { key: '30', label: 'Last 30 days', days: 30 },
  { key: '90', label: 'Last 90 days', days: 90 },
  { key: 'all', label: 'Everything we hold', days: 0 },
]

export function ComplianceView({ client, onOpen, users }: ComplianceViewProps) {
  const [acks, setAcks] = useState<DmsAckReportRow[]>([])
  const [entries, setEntries] = useState<DmsAuditEntry[]>([])
  const [windowKey, setWindowKey] = useState('30')
  const [moduleKey, setModuleKey] = useState('decisions')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [report, log] = await Promise.all([
        client.ackReport ? client.ackReport() : Promise.resolve([]),
        client.auditLog ? client.auditLog({ limit: 1000 }) : Promise.resolve([]),
      ])
      setAcks(report)
      setEntries(log)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message
        : 'The evidence could not be read.')
    } finally {
      setLoading(false)
    }
  }, [client])

  useEffect(() => { void load() }, [load])

  const since = useMemo(() => {
    const days = WINDOWS.find((w) => w.key === windowKey)?.days || 0
    if (!days) return null
    return new Date(Date.now() - days * 86400000)
  }, [windowKey])

  const inWindow = useMemo(() => entries.filter((entry) => {
    if (!since) return true
    const at = entry.at ? new Date(entry.at) : null
    return !at || Number.isNaN(at.getTime()) ? false : at >= since
  }), [entries, since])

  const visible = useMemo(() => inWindow.filter((entry) => {
    const module = auditModuleOf(entry.action)
    if (moduleKey === 'all') return true
    // The decision trail: everything an auditor asks for, minus the read log,
    // which outnumbers it by orders of magnitude.
    if (moduleKey === 'decisions') return module !== 'access'
    return module === moduleKey
  }), [inWindow, moduleKey])

  const ackStats = useMemo(() => summariseAcks(acks), [acks])
  const decisions = useMemo(
    () => inWindow.filter((e) => e.action === 'approval_decided'), [inWindow])
  const rejections = decisions.filter(
    (e) => readDetail(e).decision === 'reject').length
  const reveals = inWindow.filter((e) => e.action === 'reveal').length

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
      <p style={introStyle}>
        Who read what, who approved what, and when. Kept automatically — nobody
        can edit it, including you. You see the part of it you are allowed to.
      </p>

      {error ? <p style={errStyle}>{error}</p> : null}

      <div style={tileRowStyle}>
        <Tile
          label="Approval decisions"
          value={String(decisions.length)}
          note={decisions.length === 0 ? 'None in this period'
            : `${rejections} sent back`}
        />
        <Tile
          label="Reading confirmed"
          value={ackStats.measurable ? `${ackStats.percent}%` : '—'}
          note={ackStats.measurable
            ? `${ackStats.acknowledged} of ${ackStats.population} people`
            : 'Nobody could be worked out for any of these'}
          warn={!ackStats.measurable}
        />
        <Tile
          label="Past their due date"
          value={String(ackStats.overdue)}
          note={ackStats.overdue ? 'People who have not confirmed yet' : 'Nobody is late'}
          warn={ackStats.overdue > 0}
        />
        <Tile
          label="Hidden values shown"
          value={String(reveals)}
          note="Times a hidden detail was shown to someone entitled to it"
          warn={reveals > 0}
        />
      </div>

      {ackStats.unresolved > 0 ? (
        <p style={warnLineStyle}>
          We could not work out who {ackStats.unresolved === 1 ? 'was' : 'were'} asked
          for {ackStats.unresolved} of these, so {ackStats.unresolved === 1 ? 'it is' : 'they are'} left
          out of the percentage above. Counting them would make the figure look
          better than we can actually show.
        </p>
      ) : null}

      <div style={filterRowStyle}>
        <select value={windowKey} onChange={(e) => setWindowKey(e.target.value)}
                style={inputStyle}>
          {WINDOWS.map((w) => (
            <option key={w.key} value={w.key}>{w.label}</option>
          ))}
        </select>
        <select value={moduleKey} onChange={(e) => setModuleKey(e.target.value)}
                style={inputStyle}>
          <option value="decisions">Decisions and changes</option>
          <option value="all">Everything, including who read what</option>
          {Object.entries(AUDIT_MODULE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <span style={{ flex: 1 }} />
        <button type="button" style={btnStyle} disabled={loading}
                onClick={() => void load()}>
          {loading ? 'Reading…' : 'Refresh'}
        </button>
        <button type="button" style={btnStyle} disabled={visible.length === 0}
                onClick={() => exportCsv(visible)}>
          Export for an auditor
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <Th>When</Th><Th>Who</Th><Th>What happened</Th>
              <Th>Document</Th><Th>Details</Th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ ...tdStyle, color: T.textSecondary }}>
                  {loading ? 'Reading…' : 'Nothing happened that matches this.'}
                </td>
              </tr>
            ) : visible.map((entry) => (
              <tr key={entry.id} style={{ borderTop: `1px solid ${T.border}` }}>
                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatWhen(entry.at)}</td>
                <td style={tdStyle}>
                  {entry.user_key ? personName(users, entry.user_key) : 'The system'}
                </td>
                <td style={tdStyle}>{auditActionLabel(entry.action)}</td>
                <td style={tdStyle}>
                  {entry.document_id ? (
                    <button type="button" style={linkStyle}
                            onClick={() => onOpen(String(entry.document_id))}>
                      Open
                    </button>
                  ) : <span style={{ color: T.textFaint }}>—</span>}
                </td>
                <td style={{ ...tdStyle, fontSize: TYPE.small, color: T.textSecondary }}>
                  {describe(entry)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <footer style={{ ...introStyle, marginTop: 12 }}>
        Showing {visible.length} thing{visible.length === 1 ? '' : 's'} that happened
        {entries.length >= 1000
          ? ' · only the 1,000 most recent are loaded, so something older may not be here'
          : ''}
      </footer>
    </div>
  )
}

// ── derivation ─────────────────────────────────────────────────────────────

function readDetail(entry: DmsAuditEntry): Record<string, unknown> {
  const raw = entry.detail_json
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {}
    } catch {
      return {}
    }
  }
  return typeof raw === 'object' ? raw as Record<string, unknown> : {}
}

/** One line saying what the row actually was. The access log's detail differs
 *  per action, so this reads the few keys that carry the meaning and falls back
 *  to nothing rather than dumping raw JSON at an auditor. */
function describe(entry: DmsAuditEntry): string {
  const detail = readDetail(entry)
  const text = (key: string) => String(detail[key] ?? '').trim()
  switch (entry.action) {
    case 'approval_decided': {
      const verb = text('decision') === 'reject' ? 'Sent back' : 'Approved'
      const status = text('status')
      const comment = text('comment')
      const chain = status === 'pending' ? ' · now with the next approver'
        : status ? ` · ${status}` : ''
      return `${verb}${chain}${comment ? ` — "${comment}"` : ''}`
    }
    case 'lifecycle':
      return [text('action'), text('to')].filter(Boolean).join(' → ')
    case 'acknowledged':
      return text('version_label') || text('assignment_id')
    case 'ack_assign':
      return `${text('audience_kind')}:${text('audience_id')}`.replace(/^:$/, '')
    case 'qa_query':
      return text('answered') === 'false' ? 'Nothing they could see covered it'
        : text('question')
    case 'reveal':
      return text('field_key') || text('field') || 'a hidden detail'
    case 'acl_set':
      return [text('scope'), text('role') || text('user')].filter(Boolean).join(' · ')
    default: {
      const first = Object.entries(detail)
        .filter(([, value]) => value !== null && value !== '' && typeof value !== 'object')
        .slice(0, 2)
        .map(([key, value]) => `${key}: ${value}`)
      return first.join(' · ')
    }
  }
}

function summariseAcks(rows: DmsAckReportRow[]) {
  const now = Date.now()
  let population = 0
  let acknowledged = 0
  let unresolved = 0
  let overdue = 0
  for (const row of rows) {
    if (!row.population_resolved) {
      unresolved += 1
      continue
    }
    population += row.population.length
    acknowledged += Math.min(row.acknowledged_count, row.population.length)
    const due = row.due_at ? new Date(row.due_at).getTime() : NaN
    if (row.active && !Number.isNaN(due) && due < now) {
      overdue += row.outstanding.length
    }
  }
  return {
    measurable: population > 0,
    percent: population ? Math.round((acknowledged / population) * 100) : 0,
    population,
    acknowledged,
    unresolved,
    overdue,
  }
}

/**
 * Export what is on screen, not everything held.
 *
 * An auditor filters to the trail they are evidencing and exports that; an
 * export that quietly widened back to every row would attach evidence they did
 * not review to a finding they did.
 */
function exportCsv(rows: DmsAuditEntry[]): void {
  const header = ['timestamp', 'actor', 'ip', 'action', 'module',
                  'document_id', 'version_id', 'detail']
  const cell = (value: unknown) => {
    const text = value === null || value === undefined ? '' : String(value)
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }
  const body = rows.map((entry) => [
    entry.at, entry.user_key, entry.ip, entry.action,
    auditModuleOf(entry.action), entry.document_id, entry.version_id,
    describe(entry),
  ].map(cell).join(','))
  const blob = new Blob([[header.join(','), ...body].join('\n')],
                        { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `document-evidence-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

// ── pieces ─────────────────────────────────────────────────────────────────

function Tile({ label, value, note, warn }: {
  label: string; value: string; note: string; warn?: boolean
}) {
  return (
    <div style={tileStyle}>
      <span style={tileLabelStyle}>{label}</span>
      <strong style={{
        fontSize: 27,
        fontWeight: 600,
        color: warn ? T.error : T.accent,
      }}>
        {value}
      </strong>
      <span style={{ fontSize: TYPE.small, color: T.textSecondary, lineHeight: 1.45 }}>{note}</span>
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th style={{
      textAlign: 'left',
      padding: '9px 10px',
      fontSize: TYPE.small,
      color: T.textFaint,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {children}
    </th>
  )
}

// ── styles ─────────────────────────────────────────────────────────────────

const introStyle: React.CSSProperties = {
  margin: '0 0 14px',
  fontSize: TYPE.meta,
  color: T.textSecondary,
  lineHeight: 1.55,
}

const tileRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
  gap: 10,
  marginBottom: 14,
}

const tileStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '13px 15px',
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.lg,
  background: T.surface,
}

const tileLabelStyle: React.CSSProperties = {
  fontSize: TYPE.small,
  color: T.textSecondary,
}

const warnLineStyle: React.CSSProperties = {
  margin: '0 0 14px',
  padding: '10px 12px',
  fontSize: TYPE.meta,
  lineHeight: 1.55,
  borderRadius: RADIUS.md,
  border: `1px solid ${T.warning}`,
  background: T.warningWeak,
  color: T.warning,
}

const filterRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  marginBottom: 8,
  flexWrap: 'wrap',
}

const inputStyle: React.CSSProperties = sharedInputStyle

const btnStyle: React.CSSProperties = sharedBtnStyle

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: TYPE.meta,
  minWidth: 680,
}

const tdStyle: React.CSSProperties = {
  padding: '10px',
  verticalAlign: 'top',
  lineHeight: 1.5,
}

const linkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: TYPE.meta,
  fontFamily: 'inherit',
  color: T.accent,
  cursor: 'pointer',
}

const errStyle: React.CSSProperties = {
  margin: '0 0 12px',
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  color: T.error,
}
